import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Thin-line icon set. All 24x24, stroked with currentColor so they inherit the
// link colour and pick up the emerald tint when active.
const I = (d) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    {d}
  </svg>
);
const icons = {
  dashboard: I(<><rect x="3" y="3" width="8" height="8" rx="1.5" /><rect x="13" y="3" width="8" height="5" rx="1.5" /><rect x="13" y="11" width="8" height="10" rx="1.5" /><rect x="3" y="14" width="8" height="7" rx="1.5" /></>),
  add: I(<path d="M12 5v14M5 12h14" />),
  update: I(<path d="M4 20h4L18 10l-4-4L4 16v4z" />),
  tracker: I(<><path d="M9 3h6l1 2h3v16H5V5h3l1-2z" /><path d="M9 12h6M9 16h4" /></>),
  myjobs: I(<><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2" /></>),
  document: I(<><rect x="4" y="4" width="16" height="16" rx="2" /><path d="m4 8 8 5 8-5" /></>),
  training: I(<><path d="M2 10l10-5 10 5-10 5z" /><path d="M6 12v5c3 2 9 2 12 0v-5" /></>),
  chat: I(<path d="M21 12a8 8 0 01-11 7.4L4 21l1.6-5A8 8 0 1121 12z" />),
  present: I(<><rect x="3" y="4" width="18" height="13" rx="2" /><path d="M8 21h8M12 17v4" /></>),
  photos: I(<><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 16 5-5 4 4 3-3 6 6" /></>),
  quote: I(<path d="M7 17h3l2-4V7H6v6h3zM15 17h3l2-4V7h-6v6h3z" />),
  review: I(<><circle cx="12" cy="12" r="9" /><path d="M12 8v4l3 2" /></>),
  board: I(<><path d="M4 20V10M10 20V4M16 20v-7M22 20H2" /></>),
  staff: I(<><circle cx="9" cy="8" r="3" /><circle cx="17" cy="9" r="2.5" /><path d="M3 20a6 6 0 0112 0M15 20a4.5 4.5 0 017 0" /></>),
  sales: I(<><path d="M3 17l6-6 4 4 8-8" /><path d="M14 7h7v7" /></>),
  collection: I(<><rect x="3" y="6" width="18" height="13" rx="2" /><path d="M3 10h18" /></>),
  calendar: I(<><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></>),
  calc: I(<><rect x="5" y="3" width="14" height="18" rx="2" /><path d="M8 7h8M8 12h2M12 12h2M16 12h0M8 16h2M12 16h2M16 16h0" /></>),
  paint: I(<><path d="M4 4h12v5H4z" /><path d="M16 6h3v5H9v4" /><rect x="8" y="15" width="3" height="6" rx="1" /></>),
  contractors: I(<><path d="M3 21h18M5 21V8l7-5 7 5v13" /><path d="M10 21v-6h4v6" /></>),
  forms: I(<><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M8 8h8M8 12h8M8 16h5" /></>),
  users: I(<><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0116 0" /></>),
  money: I(<><circle cx="12" cy="12" r="9" /><path d="M12 7v10M14.5 9.5a2.5 2 0 00-5 0c0 2.5 5 1.5 5 4a2.5 2 0 01-5 0" /></>),
  shield: I(<path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" />),
  idcard: I(<><rect x="3" y="5" width="18" height="14" rx="2" /><circle cx="9" cy="11" r="2" /><path d="M6 16a3 3 0 016 0M14 10h4M14 14h4" /></>),
  change: I(<><path d="M4 7h11l-3-3M20 17H9l3 3" /></>),
  packet: I(<><path d="M4 4h10l6 6v10H4z" /><path d="M14 4v6h6" /></>),
  payroll: I(<><rect x="3" y="6" width="18" height="12" rx="2" /><circle cx="12" cy="12" r="2.5" /></>),
  logout: I(<><path d="M10 17l5-5-5-5" /><path d="M15 12H3M13 3h5a2 2 0 012 2v14a2 2 0 01-2 2h-5" /></>),
};

const nl = (to, icon, label, onClick) => (
  <NavLink to={to} className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`} onClick={onClick}>
    {icons[icon]} {label}
  </NavLink>
);

function Section({ label, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="nav-section-toggle"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', background: 'none', border: 'none', cursor: 'pointer', font: 'inherit', textAlign: 'left' }}
      >
        <span className="nav-section-label">{label}</span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" width="14" height="14"
          style={{ marginRight: 12, marginTop: 10, opacity: 0.6, transition: 'transform 0.15s', transform: open ? 'rotate(90deg)' : 'rotate(0deg)' }}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 6l6 6-6 6" />
        </svg>
      </button>
      {open && <div>{children}</div>}
    </div>
  );
}

export default function Sidebar() {
  const { username, signOut, isAdmin, isStaff, isContractor, role, staff_name } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  function handleLogout() { signOut(); navigate('/login'); }
  const close = () => setOpen(false);
  const displayName = staff_name || username || '';
  const initial = (displayName[0] || 'A').toUpperCase();

  return (
    <>
      <button className="hamburger-btn" onClick={() => setOpen(o => !o)} aria-label="Toggle menu">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22">
          {open ? <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /> : <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />}
        </svg>
      </button>

      {open && <div className="sidebar-overlay" onClick={close} />}

      <aside className={`sidebar${open ? ' open' : ''}`}>
        <div className="sidebar-logo">
          <div className="sidebar-mark">A</div>
          <div>
            <h1>Amanda's Home Improvement</h1>
            <span>Job Tracker</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          {isStaff ? (
            <>
              <div className="nav-section-label">My Work</div>
              {nl('/my-jobs', 'myjobs', 'My Jobs', close)}
              {nl('/send-contract', 'document', 'Send Document', close)}
              {nl('/training', 'training', 'Amanda\'s University', close)}
              {nl('/objections', 'chat', 'Objection Room', close)}
              {nl('/presentations', 'present', 'Presentations', close)}
              {nl('/projects', 'photos', 'See Our Work', close)}
              {nl('/testimonials', 'quote', 'What Customers Say', close)}
              {nl('/investment-review', 'review', 'Investment Review', close)}
              {nl('/calculator', 'calc', 'Job Calculator', close)}
              {nl('/painting-calculator', 'paint', 'Painting Calculator', close)}
              {nl('/board', 'board', 'Performance Board', close)}
            </>
          ) : isContractor ? (
            <>
              <div className="nav-section-label">My Work</div>
              {nl('/contractor-portal', 'myjobs', 'My Jobs', close)}
            </>
          ) : (
            <>
              <div className="nav-section-label">Overview</div>
              {nl('/', 'dashboard', 'Dashboard', close)}
              {nl('/tracker', 'tracker', 'Job Tracker', close)}
              {nl('/send-contract', 'document', 'Send Document', close)}

              {isAdmin && (
                <Section label="Records">
                  {nl('/add', 'add', 'Add Record', close)}
                  {nl('/update', 'update', 'Update Record', close)}
                </Section>
              )}

              <Section label="Amanda's University" defaultOpen={false}>
                {nl('/training', 'training', 'Training', close)}
                {nl('/objections', 'chat', 'Objection Room', close)}
                {isAdmin && nl('/training-admin', 'forms', 'University Admin', close)}
                {nl('/presentations', 'present', 'Presentations', close)}
                {nl('/projects', 'photos', 'See Our Work', close)}
                {nl('/testimonials', 'quote', 'What Customers Say', close)}
              </Section>

              <Section label="Reports" defaultOpen={false}>
                {nl('/investment-review', 'review', 'Investment Review', close)}
                {nl('/board', 'board', 'Performance Board', close)}
                {nl('/staff-report', 'staff', 'Staff Report', close)}
                {nl('/sales', 'sales', 'Sales Report', close)}
                {nl('/collection', 'collection', 'Collection', close)}
                {nl('/calendar', 'calendar', 'Calendar', close)}
                {nl('/calculator', 'calc', 'Job Calculator', close)}
                {nl('/painting-calculator', 'paint', 'Painting Calculator', close)}
              </Section>

              {isAdmin && (
                <Section label="Management" defaultOpen={false}>
                  {nl('/contractors', 'contractors', 'Contractors', close)}
                  {nl('/staff', 'staff', 'Staff', close)}
                  {nl('/forms', 'forms', 'Forms List', close)}
                  {nl('/users', 'users', 'Users', close)}
                  {nl('/quickbooks', 'money', 'QuickBooks', close)}
                  {nl('/data-safety', 'shield', 'Data Safety', close)}
                  {nl('/id-cards', 'idcard', 'ID Cards', close)}
                  {nl('/change-orders', 'change', 'Change Orders', close)}
                  {nl('/contractor-packets', 'packet', 'Contractor Packets', close)}
                  {nl('/canvasser-payroll', 'payroll', 'Canvasser Payroll', close)}
                </Section>
              )}
            </>
          )}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-avatar">{initial}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: 'var(--gray-900)', fontWeight: 500, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayName}</div>
            <div style={{ fontSize: 11.5, textTransform: 'capitalize' }}>{role}</div>
          </div>
          <button className="btn btn-ghost btn-sm" title="Sign out" aria-label="Sign out" onClick={handleLogout} style={{ padding: '0 6px' }}>
            {icons.logout}
          </button>
        </div>
      </aside>
    </>
  );
}
